import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
}

function formatMonth(period) {
  if (!period) return '';
  const [year, month] = period.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month) - 1]} ${year}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="report-chart-tooltip">
      <p className="report-chart-tooltip-label">{formatMonth(label)}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

export default function ForecastChart({ data, groupBy, onDataPointClick }) {
  if (!data || data.length === 0) {
    return <div className="report-chart-empty">No forecast data for the selected period.</div>;
  }

  // Pivot data: period on X axis, group_label as bars with actual + weighted
  const groups = [...new Set(data.map(d => d.group_label).filter(Boolean))];
  const periods = [...new Set(data.map(d => d.period))].sort();

  const chartData = periods.map(period => {
    const entry = { period };
    groups.forEach(group => {
      const match = data.find(d => d.period === period && d.group_label === group);
      entry[`${group}_total`] = match ? match.total_amount : 0;
      entry[`${group}_weighted`] = match ? match.weighted_amount : 0;
    });
    // Also compute totals for simpler view
    const periodRows = data.filter(d => d.period === period);
    entry.total_amount = periodRows.reduce((sum, r) => sum + (r.total_amount || 0), 0);
    entry.weighted_amount = periodRows.reduce((sum, r) => sum + (r.weighted_amount || 0), 0);
    return entry;
  });

  const handleClick = (entry) => {
    if (onDataPointClick) {
      onDataPointClick({ period: entry.period });
    }
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis
          dataKey="period"
          tickFormatter={formatMonth}
          tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="total_amount"
          name="Pipeline"
          fill="#3b82f6"
          opacity={0.4}
          cursor="pointer"
          onClick={(entry) => handleClick(entry)}
        />
        <Bar
          dataKey="weighted_amount"
          name="Weighted Forecast"
          fill="#22c55e"
          cursor="pointer"
          onClick={(entry) => handleClick(entry)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
